import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { AuthoringStatus } from "@keydials/api-client";
import { api, getAccessToken, useAuth } from "../auth/AuthContext";
import { offerBuilder } from "./builderGate";

interface AuthoringState {
  /** The server's answer, or null before it arrives and whenever the probe failed. */
  status: AuthoringStatus | null;
  /** False while the probe for the signed-in person is in flight, so nothing flashes a way in and then withdraws it. */
  loaded: boolean;
  /** Whether to offer the builder: the fail-closed answer every way in and the route share. */
  offered: boolean;
}

const AuthoringContext = createContext<AuthoringState | undefined>(undefined);

/**
 * Whether this person can build a Key Dial here (KEY-560), probed once per signed-in person and shared by the builder's route and
 * every way into it, so they can't disagree. A failed probe leaves the builder switched off: offering a door the server will
 * only refuse is worse than not offering it.
 */
export function AuthoringProvider({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  const [status, setStatus] = useState<AuthoringStatus | null>(null);
  const [loaded, setLoaded] = useState(false);
  const userId = me?.userId ?? null;

  useEffect(() => {
    setStatus(null);
    const token = getAccessToken();
    if (!userId || !token) {
      setLoaded(true);
      return;
    }
    setLoaded(false);
    let cancelled = false;
    api
      .getAuthoringStatus(token)
      .then((answer) => {
        if (!cancelled) setStatus(answer);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const value = useMemo<AuthoringState>(() => ({ status, loaded, offered: offerBuilder({ status, me }) }), [status, loaded, me]);

  return <AuthoringContext.Provider value={value}>{children}</AuthoringContext.Provider>;
}

export function useAuthoring(): AuthoringState {
  // No provider means no builder, never a thrown page: the rest of the app works without it.
  return useContext(AuthoringContext) ?? { status: null, loaded: true, offered: false };
}
